import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { EmptyApiObject } from 'api/api-models';
import { FeedApi } from 'api/feed/feed-api';
import { AuthService } from 'lib/auth/auth.service';
import { map, Observable, Subscription } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { IMenuItem, ITypeParentRoute } from 'src/app/pages/type-routes/type-route.models';
import { TimeService } from 'src/app/services/time.service';

export interface ExtendedFeed extends FeedApi.ILatestFeed {
	readonly avatarName: string;
	readonly fromNow: string;
	readonly isNew: boolean;
}

export interface IParentRouteWithState extends ITypeParentRoute {
	isActive: boolean;
	readonly id: string;
}

export interface IMenuItemWithState extends IMenuItem {
	readonly id: string;
	readonly isActive: boolean;
}

@Injectable({ providedIn: 'root' })
export class LayoutService {
	private readonly destroyRef = inject(DestroyRef);

	public readonly feeds = signal<readonly ExtendedFeed[]>([]);
	public readonly unreadFeedsCount = computed<number>(() => {
		return this.feeds().filter((feed) => !feed.isRead).length;
	});

	constructor(
		private readonly httpService: HttpService,
		private readonly authService: AuthService,
		private readonly timeService: TimeService,
		private readonly router: Router
	) {
		effect(() => {
			const authUser = this.authService.authUser();

			if (authUser) {
				// init feeds when user is authenticated
				this.loadFeeds();
			} else {
				// clear feeds
				this.feeds.set([]);
			}
		});
	}

	getMenuItemsWithState(menuItems: readonly IMenuItem[]): readonly IMenuItemWithState[] {
		return menuItems.map<IMenuItemWithState>((menuItem) => {
			return {
				...menuItem,
				id: `${menuItem.title}-${menuItem.routerLink}-${menuItem.iconType}`,
				isActive: this.isMenuItemActive(menuItem)
			};
		});
	}

	getParentRoutesWithState(parentRoutes: readonly ITypeParentRoute[]): readonly IParentRouteWithState[] {
		if (!parentRoutes) {
			return [];
		}

		const enhancedParentRoutes: readonly IParentRouteWithState[] = parentRoutes.map<IParentRouteWithState>((m) => {
			return {
				...m,
				id: `${m.title}-${m.routerLink}`,
				isActive: this.isParentRouteActive(m)
			};
		});

		// highlight only latest entry if there are items with same router link
		let isActiveSet: boolean = false;
		if (enhancedParentRoutes.length) {
			for (let i = enhancedParentRoutes.length - 1; i--; i >= 0) {
				const route = enhancedParentRoutes[i];
				if (route.isActive && !isActiveSet) {
					isActiveSet = true;
					route.isActive = false;
				}
			}
		}

		return enhancedParentRoutes;
	}

	markFeedsAsRead(): void {
		const feeds = this.feeds();

		if (!feeds.length) {
			return;
		}

		this.subscribeToObservable(
			this.httpService
				.post<EmptyApiObject, FeedApi.IMarkFeedsAsReadRequest>((builder) =>
					builder
						.withCustomApiRoute('feed', 'markFeedsAsRead')
						.withData({
							feedIds: feeds.map((m) => m.id)
						})
						.build()
				)
				.pipe(
					map(() => {
						this.feeds.set(this.feeds().map((m) => ({ ...m, isRead: true })));
					})
				)
		);
	}

	markAllFeedsAsRead(userId: string): void {
		this.subscribeToObservable(
			this.httpService
				.post<EmptyApiObject, FeedApi.IMarkAllFeedsAsReadRequest>((builder) =>
					builder
						.withCustomApiRoute('feed', 'markAllFeedsAsRead')
						.withData({
							userId: userId
						})
						.build()
				)
				.pipe(
					map(() => {
						this.feeds.set(this.feeds().map((m) => ({ ...m, isRead: true })));
					})
				)
		);
	}

	getLoadFeedsObservable(): Observable<readonly ExtendedFeed[]> {
		return this.httpService
			.get<FeedApi.ILatestFeeds>((builder) => builder.withCustomApiRoute('feed', 'latestFeeds').build())
			.pipe(
				map((response) => {
					return response.data.feeds.map<ExtendedFeed>((feed) => {
						return {
							...feed,
							fromNow: this.timeService.fromNow(feed.created),
							avatarName: feed.originUserName,
							isNew: this.isFeedNew(feed)
						};
					});
				})
			);
	}

	loadFeeds(): void {
		this.subscribeToObservable(this.getLoadFeedsObservable());
	}

	private isMenuItemActive(menuItem: IMenuItem): boolean {
		if (menuItem.routerLink.toLowerCase() === this.router.url.toLowerCase()) {
			return true;
		}
		return false;
	}

	private isParentRouteActive(parentRoute: ITypeParentRoute): boolean {
		if (parentRoute.routerLink.toLowerCase() === this.router.url.toLowerCase()) {
			return true;
		}
		return false;
	}

	private isFeedNew(feed: FeedApi.ILatestFeed): boolean {
		if (feed.isRead) {
			return false;
		}

		const differentInMinutes = this.timeService.getDifferenceInMinutes(feed.created, this.timeService.now());
		return differentInMinutes < 5;
	}

	private subscribeToObservable(observable: Observable<any>): Subscription {
		return observable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
	}
}
